import React, { useContext } from 'react';
import { View } from 'react-native';
import PropTypes from 'prop-types';
import { formatTime, itemHasEnded, getProgressPercentage, getAssetTitle } from 'Utils';
import { ConfirmationModalContext, NoDataText } from 'LocalGluons';
import { t } from 'Locale';
import {
    filterOutAdultItemsByCategories,
    isContentBlocked,
    isContentHidden,
    isContentLocked
} from 'Utils/parentalControlUtils';
import { Device } from 'DeviceUtils';
import { useStore } from 'Store/Store';
import ProgramItem from '../ProgramItem';
import ActivityIndicator from '../ActivityIndicator';

const addCategoryWithSeparator = (category) => {
    if (!category) return '';
    return ` | ${category}`;
};

const createProgramDescription = (item = {}) => {
    const startTime = item.getStartTime();
    const endTime = item.getEndTime();
    let subtitle =
        startTime && endTime
            ? `${formatTime(item.getStartTime())}-${formatTime(item.getEndTime())}`
            : '';
    subtitle += addCategoryWithSeparator(item.getCategory());

    if (subtitle.length > 30) {
        subtitle = `${subtitle.slice(0, Device.isTablet ? 20 : 35)}... `;
    }

    return {
        subtitle,
        title: getAssetTitle(item),
        progress: getProgressPercentage(item)
    };
};

// eslint-disable-next-line react/no-multi-comp, react/prefer-stateless-function
const ProgramList = ({ style, items, isLoading, onSelectChannel, onSelectProgram }) => {
    const { setProtectedContent } = useContext(ConfirmationModalContext);
    const { parentalLockConfig, categories } = useStore();
    const filteredItems = filterOutAdultItemsByCategories(items, categories, parentalLockConfig);

    const renderItems = () =>
        filteredItems.map((item, i) => {
            const { title, subtitle, progress } = createProgramDescription(item);

            const isAdult = item.isAdult();
            const shouldBeHiddenFromChannels = isContentHidden(isAdult, parentalLockConfig);
            const adultLock = isContentLocked(isAdult, parentalLockConfig);
            const ageBlock = isContentBlocked(item.data?.parentalRating, parentalLockConfig);
            const blocked = adultLock || ageBlock;
            const iconProps = {};

            iconProps.iconUrl = item.data?.channelLogoUrl || '';
            if (ageBlock) {
                iconProps.iconName = 'lock';
            }

            const itemId = item.getId();

            const key = title && itemId ? `${title}_${item.getChannelId()}_${itemId}_${i}` : `${i}`;
            return shouldBeHiddenFromChannels ? null : (
                <ProgramItem
                    key={key}
                    onIconPress={() => {
                        if (blocked) {
                            setProtectedContent(item, () => onSelectChannel(item));
                        } else {
                            onSelectChannel(item);
                        }
                    }}
                    onDescriptionPress={() => {
                        if (blocked) {
                            setProtectedContent(item, () => onSelectProgram(item));
                        } else {
                            onSelectProgram(item);
                        }
                    }}
                    title={adultLock ? t('COMMON.LOCKED_ADULT_CONTENT') : title}
                    subTitle={subtitle}
                    progress={itemHasEnded(item) ? null : progress}
                    isContentLocked={blocked}
                    isContentHidden={adultLock}
                    {...iconProps}
                    testID={`channel_position_${i}`}
                />
            );
        });

    const renderNoDataText = () => (isLoading ? null : <NoDataText />);

    return (
        <View style={style}>
            {filteredItems.length > 0 ? renderItems() : renderNoDataText()}
            {isLoading && (
                <View style={{ marginTop: 5 }}>
                    <ActivityIndicator />
                </View>
            )}
        </View>
    );
};

ProgramList.defaultProps = {
    style: {},
    items: [],
    isLoading: false,
    onSelectChannel: () => {},
    onSelectProgram: () => {}
};

ProgramList.propTypes = {
    style: PropTypes.shape({}),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    isLoading: PropTypes.bool,
    onSelectChannel: PropTypes.func,
    onSelectProgram: PropTypes.func
};

export default ProgramList;
